import { NotificationsOutlined, InfoOutlined, AccessTime } from '@mui/icons-material';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorState } from '@/components/common';
import { useNotifications, useMarkAsRead } from '@/hooks/queries/useNotifications';
import { useEnrichedNotifications } from '@/hooks/useEnrichedNotifications';
import type { Notification } from '@/types/notifications';

export const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch only unread in-app notifications with limit of 5 for dropdown
  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useNotifications({
    isRead: false,
    type: 'in-app',
    limit: 5,
    orderDirection: 'DESC',
  });

  const markAsReadMutation = useMarkAsRead();

  const allNotifications = notificationsData?.data || [];
  const unreadCount = notificationsData?.total || 0;

  // Enrich notifications with user and organization data
  const { enrichedNotifications, isLoading: isEnriching } =
    useEnrichedNotifications(allNotifications);
  const recentNotifications = enrichedNotifications;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getNotificationIcon = (type: string) => {
    // Map backend notification types to UI types
    switch (type) {
      case 'in-app':
        return <InfoOutlined className="text-blue-500" fontSize="small" />;
      case 'email':
        return <InfoOutlined className="text-green-500" fontSize="small" />;
      case 'push':
        return <NotificationsOutlined className="text-purple-500" fontSize="small" />;
      default:
        return <InfoOutlined className="text-blue-500" fontSize="small" />;
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return `Hace ${diffDays}d`;
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    try {
      await markAsReadMutation.mutateAsync({
        notificationId: notification.id,
        data: { isRead: true },
      });
    } catch (_error) {
      // Handle error silently
    }

    // Handle action based on notification data
    const action = notification.data?.['action'];
    if (action) {
      // Handle specific actions based on notification metadata
      setIsOpen(false);
      // You can add navigation logic here based on action
    }
  };

  const handleViewAllNotifications = () => {
    setIsOpen(false);
    void navigate('/notificaciones');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative text-navbar-text p-1 hover:text-navbar-hover focus:outline-none rounded-md cursor-pointer"
        disabled={isLoading}
      >
        <NotificationsOutlined />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-5 w-5 text-xs leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-md">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-brand-white">Notificaciones</h3>
              <span className="text-sm font-medium text-muted">{unreadCount} nuevas</span>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading || isEnriching ? (
              <div className="px-4 py-8 text-center text-muted">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-white mx-auto"></div>
                <p className="mt-2 text-sm">Cargando...</p>
              </div>
            ) : error ? (
              <div className="px-4 py-4">
                <ErrorState
                  title="Error al cargar"
                  message="No se pudieron cargar las notificaciones"
                  error={error}
                  showRetry
                  onRetry={() => void refetch()}
                  useCard={false}
                  iconSize={32}
                />
              </div>
            ) : recentNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-muted">
                <NotificationsOutlined className="mx-auto mb-2 text-muted" fontSize="large" />
                <p className="text-sm">No hay notificaciones nuevas</p>
              </div>
            ) : (
              recentNotifications.map(notification => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => void handleNotificationClick(notification)}
                  className="w-full px-4 py-3 border-b border-border hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer bg-black/5 dark:bg-white/5 text-left"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brand-white mb-1">{notification.title}</p>
                      <p className="text-sm text-muted line-clamp-2">{notification.message}</p>
                      {(notification.user || notification.organization) && (
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted">
                          {notification.user && (
                            <span className="flex items-center">
                              <span className="font-medium">{notification.user.name}</span>
                            </span>
                          )}
                          {notification.user && notification.organization && <span>•</span>}
                          {notification.organization && (
                            <span>{notification.organization.name}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center mt-2 text-xs text-muted">
                        <AccessTime fontSize="inherit" className="mr-1" />
                        {formatTimeAgo(notification.createdAt)}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border">
            <button
              onClick={handleViewAllNotifications}
              className="w-full text-center text-sm text-muted hover:text-brand-white cursor-pointer font-medium transition-colors"
            >
              Ver todas las notificaciones
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
